import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  IconButton,
  Backdrop,
  LinearProgress,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  MonetizationOn as MonetizationOnIcon,
  Edit,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  createTravelTime,
  getTravelTime,
  updateTravelTime,
} from "../../redux/action/rate";

const TravelTime: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [chargeType, setChargeType] = useState("");
  const [isDoubleTravelTime, setIsDoubleTravelTime] = useState(false);
  const [rate, setRate] = useState("");
  const [editableField, setEditableField] = useState<string | null>(null);
  const [isTravelTimeExists, setIsTravelTimeExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [carrierId, setCarrierId] = useState<number | null>(null);
  useEffect(() => {
    const storedCarrierId = localStorage.getItem("CarrierId");
    if (storedCarrierId) {
      const carrierIdNumber = parseInt(storedCarrierId, 10);
      if (!isNaN(carrierIdNumber)) {
        setCarrierId(carrierIdNumber);
      } else {
        console.error("Invalid CarrierId in localStorage");
      }
    }
  }, []);
  useEffect(() => {
    if (carrierId) {
      dispatch<any>(getTravelTime(carrierId))
        .then(unwrapResult)
        .then((res: any) => {
          if (res) {
            setIsTravelTimeExists(true);
            setChargeType(res.charge_type || "");
            setIsDoubleTravelTime(res.isdouble_travel_time || false);
            setRate(res.rate?.toString() || "");
          } else {
            setIsTravelTimeExists(false);
          }
        })
        .catch((err: any) => {
          setLoadingMessage("Failed to fetch travel time data.");
          console.error("Error fetching travel time:", err);
        });
    }
  }, [carrierId, dispatch]);

  const handleSubmit = async () => {
    setIsLoading(true);
    setLoadingMessage("Saving changes. Please wait...");

    const travelTimeData = {
      carrier_id: carrierId,
      charge_type: chargeType,
      isdouble_travel_time: isDoubleTravelTime,
      rate: isDoubleTravelTime ? parseFloat(rate) : 0,
    };

    try {
      let response;
      if (isTravelTimeExists) {
        response = await dispatch<any>(updateTravelTime(travelTimeData));
      } else {
        response = await dispatch<any>(createTravelTime(travelTimeData));
      }
      const result = unwrapResult(response);
      setLoadingMessage("Changes saved successfully!");
      setEditableField(null);
      setIsTravelTimeExists(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 1500);
    } catch (err: any) {
      setLoadingMessage("Failed to save changes.");
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Typography
        sx={{
          fontWeight: "bold",
          mb: 4,
          fontSize: isMobile ? "1.5rem" : "2rem",
        }}
      >
        Travel Time
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
        }}
      >
        <Box sx={{ flex: "1 1 45%", minWidth: "240px", position: "relative" }}>
          <TextField
            fullWidth
            label="Charge Type"
            variant="outlined"
            value={chargeType}
            onChange={(e) => setChargeType(e.target.value)}
            disabled={isTravelTimeExists && editableField !== "chargeType"}
            slotProps={{
              input: {
                startAdornment: (
                  <AccessTimeIcon
                    sx={{ mr: 1, color: theme.palette.primary.main }}
                  />
                ),
              },
            }}
          />
          {isTravelTimeExists && editableField !== "chargeType" && (
            <IconButton
              onClick={() => setEditableField("chargeType")}
              sx={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <Edit />
            </IconButton>
          )}
        </Box>

        <Box sx={{ flex: "1 1 100%", minWidth: "240px", position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDoubleTravelTime}
                  onChange={(e) => setIsDoubleTravelTime(e.target.checked)}
                  color="primary"
                  sx={{ mr: 1 }}
                />
              }
              label="Can you offer double travel time?"
              disabled={
                isTravelTimeExists && editableField !== "isDoubleTravelTime"
              }
            />
            {isTravelTimeExists && editableField !== "isDoubleTravelTime" && (
              <IconButton
                onClick={() => setEditableField("isDoubleTravelTime")}
                sx={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <Edit />
              </IconButton>
            )}
          </Box>
        </Box>

        {isDoubleTravelTime && (
          <Box
            sx={{ flex: "1 1 45%", minWidth: "240px", position: "relative" }}
          >
            <TextField
              fullWidth
              label="Rate"
              variant="outlined"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              disabled={isTravelTimeExists && editableField !== "rate"}
              slotProps={{
                input: {
                  startAdornment: (
                    <MonetizationOnIcon
                      sx={{ mr: 1, color: theme.palette.primary.main }}
                    />
                  ),
                },
              }}
            />
            {isTravelTimeExists && editableField !== "rate" && (
              <IconButton
                onClick={() => setEditableField("rate")}
                sx={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <Edit />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{ px: 4, py: 2 }}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export default TravelTime;
